import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DashboardIcon,
  TransactionIcon,
  WalletIcon,
  UserIcon,
  SettingsIcon,
  ChartIcon,
  LogoutIcon,
} from "./Icons";
import { useTheme } from "../context/ThemeContext";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  const menuItems = [
    { icon: DashboardIcon, label: "Dashboard", path: "/dashboard" },
    { icon: ChartIcon, label: "Trade History", path: "/profit-summary" },
    { icon: TransactionIcon, label: "Trade Logs", path: "/trade-logs" },
    { icon: ChartIcon, label: "Trading", path: "/arbitrage" },
    {
      icon: ChartIcon,
      label: "Trading Performance",
      path: "/trading-performance",
    },
    { icon: UserIcon, label: "Direct Referrals", path: "/my-direct" },
    { icon: UserIcon, label: "Team Structure", path: "/mlm-team" },
    { icon: UserIcon, label: "Team Tree View", path: "/mlm-team-tree" },
    { icon: ChartIcon, label: "Level Details", path: "/mlm-level-details" },
    {
      icon: ChartIcon,
      label: "Level-wise Details",
      path: "/level-wise-business",
    },
    {
      icon: ChartIcon,
      label: "Direct Leg Business",
      path: "/direct-leg-business",
    },
    { icon: TransactionIcon, label: "Transactions", path: "/transactions" },
    { icon: WalletIcon, label: "Incentives & Rewards", path: "/income-bonus" },
    { icon: WalletIcon, label: "Withdrawal", path: "/withdrawal" },
    { icon: TransactionIcon, label: "Reports", path: "/reports" },
    { icon: WalletIcon, label: "Wallet", path: "/wallet" },
    { icon: UserIcon, label: "Profile & Security", path: "/profile-security" },
    { icon: SettingsIcon, label: "Settings", path: "/settings" },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    try {
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        setCurrentUser(JSON.parse(rawUser));
      }
    } catch (error) {
      console.error("Failed to parse user from storage:", error);
    }
  }, []);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandaloneMode(isStandalone);
    if (isStandalone) {
      setCanInstall(false);
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setCanInstall(false);
      setInstallPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const displayName = useMemo(() => {
    const name = currentUser?.name;
    if (typeof name === "string" && name.trim()) return name.trim();
    return "User";
  }, [currentUser]);

  const displayRole = useMemo(() => {
    const role = currentUser?.role;
    if (typeof role === "string" && role.trim()) {
      const normalized = role.trim().replace(/_/g, " ");
      return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return "Member";
  }, [currentUser]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleInstallApp = async () => {
    if (installPromptEvent) {
      installPromptEvent.prompt();
      await installPromptEvent.userChoice;
      setInstallPromptEvent(null);
      setCanInstall(false);
      return;
    }

    const userAgent = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);

    if (isIOS) {
      window.alert(
        "To install on iPhone/iPad: tap Share button in Safari, then tap 'Add to Home Screen'.",
      );
      return;
    }

    if (isAndroid) {
      window.alert(
        "To install on Android: open browser menu (three dots) and tap 'Install app' or 'Add to Home screen'.",
      );
      return;
    }

    window.alert(
      "If install prompt is not available, use your browser menu and choose 'Install app' (or click install icon in address bar).",
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--color-primary)] transition-all duration-300 hover:brightness-110 lg:hidden"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        )}
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-overlay z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 z-40 h-screen w-64 transform border border-[var(--border-color)] bg-[var(--bg-sidebar)] transition-transform duration-300 ease-in-out lg:left-0 lg:right-auto lg:border-r lg:border-l-0 lg:translate-x-0 right-0 border-l ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="border-b border-[var(--border-color)] p-6">
            <div className="flex items-center space-x-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--bg-card)] shadow-lg shadow-primary/15">
                <img
                  src="/logo-icon.png"
                  alt="Aiarbix Logo"
                  className="h-14 w-14 object-contain"
                />
              </div>
              <div>
                <h1 className="text-lg font-bold text-[var(--text-primary)]">
                  Aiarbix
                </h1>
                <p className="text-xs text-[var(--text-secondary)]">
                  Trading Dashboard
                </p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className="mt-4 flex w-full items-center justify-center rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-medium text-[var(--color-primary)] transition-all duration-300 hover:bg-primary/20"
            >
              {isDark ? "Switch to Light Theme" : "Switch to Dark Theme"}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? "bg-primary-gradient text-white shadow-lg shadow-primary/30"
                    : "text-[var(--text-secondary)] hover:bg-primary/10 hover:text-[var(--color-primary)]"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User Section */}
          <div className="border-t border-[var(--border-color)] p-4">
            <div className="flex items-center space-x-3 mb-3 px-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-card)]">
                <UserIcon className="h-5 w-5 text-[var(--color-primary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                  {displayName}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {displayRole}
                </p>
                {currentUser?.userNumber ? (
                  <p className="font-mono text-[11px] text-[var(--text-secondary)] opacity-90">
                    User ID {currentUser.userNumber}
                  </p>
                ) : null}
              </div>
            </div>
            {!isStandaloneMode && (
              <button
                onClick={handleInstallApp}
                className="mb-2 flex w-full items-center justify-center space-x-2 rounded-xl bg-primary-gradient px-4 py-2 font-semibold text-white shadow-md shadow-primary/25 transition-all duration-300 hover:brightness-110"
              >
                <span className="text-sm">
                  {canInstall ? "Install App" : "Install App (Guide)"}
                </span>
              </button>
            )}
            <button
              onClick={() => {
                // Clear all authentication tokens and user data
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("user");
                // Navigate to login
                handleNavigation("/login");
              }}
              className="flex w-full items-center justify-center space-x-2 rounded-xl border border-red-400/30 bg-red-500/5 px-4 py-2 text-red-400 transition-all duration-300 hover:bg-red-500/10"
            >
              <LogoutIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
